import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateCategoryLookupListFormValues } from '../../../../../interfaces/lookup/category';
import { IUpdateCategory } from '../../../../../interfaces/lookup/category/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchCategoryById, updateCategory } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const CategoryLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateCategoryLookupListFormValues) => {
        const body: IUpdateCategory = {
            id: lookupSlice.detailsPage.category.data?.id,
            ...(values as any),
        };

        dispatch(updateCategory(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.category.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/category', { replace: true });
        }
    }, [lookupSlice.detailsPage.category.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchCategoryById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.category.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.category.data.id,
                code: lookupSlice.detailsPage.category.data.code,
                description: lookupSlice.detailsPage.category.data.description,
            } as IUpdateCategoryLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.category.data]);

    if (lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="category-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage category lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Category',
                        path: '/admin/lookup-lists/category',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.category.data?.description || '-',
                        path: `/admin/lookup-lists/category/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/category/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.category.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.category.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.category.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.category.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.category.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.category.fetchStatus} about={'Category'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Category Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input maxLength={10} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.category?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.category.data?.created
                                            ? dayjs(lookupSlice.detailsPage.category.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.category.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.category.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.category.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
